import { Fragment, useState } from "react";
import { Link, router } from "@inertiajs/react";
import { ChevronDown } from "lucide-react";
import TableSkeleton from "./TableSkeleton";
import CardSkeleton from "./CardSkeleton";
import { cn } from "../utils/cn";

export interface DataTableColumn<T> {
  label: string;
  render: (row: T) => React.ReactNode;
  minWidth?: string;
  cellClassName?: string;
  clamp?: boolean;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => React.Key;
  rowHref?: (row: T) => string;
  expandedContent?: (row: T) => React.ReactNode;
  emptyMessage?: string;
  loading?: boolean;
}

const DataTable = <T,>({
  columns,
  rows,
  rowKey,
  rowHref,
  expandedContent,
  emptyMessage = "No results found",
  loading = false,
}: DataTableProps<T>) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<React.Key>>(new Set());

  const columnOffset = expandedContent ? 1 : 0;
  const totalColumns = columns.length + columnOffset;
  const lastIndex = totalColumns - 1;

  const [titleColumn, ...detailColumns] = columns;

  const cornerClass = (index: number) =>
    index === 0 ? "rounded-l-xl" : index === lastIndex ? "rounded-r-xl" : "";

  const toggleRow = (key: React.Key) =>
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const renderValue = (column: DataTableColumn<T>, row: T) =>
    column.clamp === false ? (
      column.render(row)
    ) : (
      <div className="line-clamp-2">{column.render(row)}</div>
    );

  const expandButton = (key: React.Key, expanded: boolean) => (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        toggleRow(key);
      }}
      aria-expanded={expanded}
      aria-label={expanded ? "Collapse row" : "Expand row"}
      className="text-cream-700 hover:text-cream-950 dark:text-cream-300 dark:hover:text-cream-100 flex cursor-pointer items-center"
    >
      <ChevronDown
        size={16}
        strokeWidth={1.5}
        className={`transition-transform ${expanded ? "rotate-180" : ""}`.trim()}
      />
    </button>
  );

  const cards = (
    <ul className="flex w-full flex-col gap-4 lg:hidden" aria-busy={loading}>
      {loading && <CardSkeleton fields={detailColumns.length} cards={3} />}
      {!loading && rows.length === 0 && (
        <li className="dark:bg-cream-950 text-cream-600 dark:text-cream-400 w-full rounded-2xl bg-white p-5 font-medium shadow">
          {emptyMessage}
        </li>
      )}
      {!loading &&
        rows.map((row) => {
          const key = rowKey(row);
          const expanded = expandedKeys.has(key);
          const title = titleColumn && renderValue(titleColumn, row);

          const details = detailColumns.length > 0 && (
            <dl className="divide-cream-200 dark:divide-cream-800 flex flex-col divide-y">
              {detailColumns.map((column) => (
                <div
                  key={column.label}
                  className="flex items-baseline justify-between gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <dt className="text-cream-600 dark:text-cream-400 shrink-0 text-sm">
                    {column.label}
                  </dt>
                  <dd
                    className={`min-w-0 text-right ${column.cellClassName ?? ""}`.trim()}
                  >
                    {renderValue(column, row)}
                  </dd>
                </div>
              ))}
            </dl>
          );

          const cardClassName =
            "dark:bg-cream-950 flex w-full flex-col gap-4 rounded-2xl bg-white p-5 shadow";

          if (rowHref && !expandedContent) {
            return (
              <li key={key}>
                <Link href={rowHref(row)} className={`${cardClassName} block`}>
                  <div
                    className={cn(titleColumn?.cellClassName, "font-semibold")}
                  >
                    {title}
                  </div>
                  {details}
                </Link>
              </li>
            );
          }

          return (
            <li key={key} className={cardClassName}>
              <div className="flex items-start justify-between gap-4">
                {rowHref ? (
                  <Link
                    href={rowHref(row)}
                    className="min-w-0 flex-1 font-semibold"
                  >
                    {title}
                  </Link>
                ) : (
                  <div className="min-w-0 flex-1 font-semibold">{title}</div>
                )}
                {expandedContent && expandButton(key, expanded)}
              </div>
              {details}
              {expandedContent && expanded && (
                <div className="border-cream-200 dark:border-cream-800 border-t pt-4">
                  {expandedContent(row)}
                </div>
              )}
            </li>
          );
        })}
    </ul>
  );

  const table = (
    <div
      className="hidden w-full overflow-x-auto rounded-2xl bg-white p-5 shadow lg:block dark:bg-black"
      aria-busy={loading}
    >
      <table className="w-full table-auto">
        <thead className="bg-peach-100 dark:bg-peach-900 text-left">
          <tr>
            {expandedContent && (
              <th className="text-cream-800 dark:text-cream-200 w-10 rounded-l-xl p-3 font-semibold">
                <span className="sr-only">Expand</span>
              </th>
            )}
            {columns.map((column, index) => (
              <th
                key={column.label}
                className={`text-cream-800 dark:text-cream-200 p-3 font-semibold ${cornerClass(index + columnOffset)} ${column.minWidth ?? ""}`.trim()}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <TableSkeleton columns={totalColumns} rows={rows.length || 3} />
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={totalColumns} className="px-3 py-8">
                <span className="text-cream-600 dark:text-cream-400 sticky left-3 inline-block font-medium">
                  {emptyMessage}
                </span>
              </td>
            </tr>
          )}
          {!loading &&
            rows.map((row) => {
              const key = rowKey(row);
              const expanded = expandedKeys.has(key);
              const handleRowClick = rowHref
                ? () => router.visit(rowHref(row))
                : expandedContent
                  ? () => toggleRow(key)
                  : undefined;

              return (
                <Fragment key={key}>
                  <tr
                    className={`hover:bg-peach-200 dark:hover:bg-peach-800 border-cream-200 dark:border-cream-900 border-b last:border-transparent hover:border-transparent ${
                      expanded
                        ? "border-transparent"
                        : "has-[+tr:hover]:border-transparent"
                    } ${handleRowClick ? "cursor-pointer" : ""}`.trim()}
                    onClick={handleRowClick}
                  >
                    {expandedContent && (
                      <td className="rounded-l-xl px-3 py-5">
                        {expandButton(key, expanded)}
                      </td>
                    )}
                    {columns.map((column, index) => (
                      <td
                        key={column.label}
                        className={`px-3 py-5 ${cornerClass(index + columnOffset)} ${column.cellClassName ?? ""}`.trim()}
                      >
                        {renderValue(column, row)}
                      </td>
                    ))}
                  </tr>
                  {expandedContent && expanded && (
                    <tr className="border-cream-200 dark:border-cream-900 border-b last:border-transparent has-[+tr:hover]:border-transparent">
                      <td colSpan={totalColumns} className="px-3 pb-5">
                        {expandedContent(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      {cards}
      {table}
    </>
  );
};

export default DataTable;
